import { ActivatedRoute } from '@angular/router';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TrackerService } from '@app/shared/services/tracker.service';
import { forkJoin } from 'rxjs';

/**
 * Temp DTO until we figure out how we'll get this info from somewhere other than the cshtml page
 */
export interface AccountDTO {
  osanoCookieBannerEnabled: boolean;
  allowEmptyEmail: boolean;
  captchaKey: string;
  errors: string;
  firstName: string;
  hasUnsubscribed: boolean;
  isForgotPassword: boolean;
  isLogin: boolean;
  isSignup: boolean;
  loginScope: string;
  message: string;
  mobileSkip: boolean;
  returnUrl: string;
  specialInstructions: string;
  ssoId: string;
  unsubscribeEmailAddress: boolean;
  unsubscribeNewsletter: boolean;
  username: string;
}

@Component({
  selector: 'dgx-account-forms',
  templateUrl: './account-forms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFormsComponent implements OnInit {
  @Input() public allowEmptyEmail: boolean = this.accountDTO.allowEmptyEmail;
  @Input() public captchaKey: string = this.accountDTO.captchaKey;
  @Input() public errors: any = this.accountDTO.errors;
  @Input() public firstName: string = this.accountDTO.firstName;
  @Input() public hasUnsubscribed: boolean = this.accountDTO.hasUnsubscribed;
  @Input() public isForgotPassword: boolean = this.accountDTO.isForgotPassword;
  @Input() public isLogin: boolean = this.accountDTO.isLogin;
  @Input() public isSignup: boolean = this.accountDTO.isSignup;
  @Input() public loginScope: string = this.accountDTO.loginScope;
  @Input() public message: any = this.accountDTO.message;
  @Input() public mobileSkip: any = this.accountDTO.mobileSkip;
  @Input() public returnUrl: string = this.accountDTO.returnUrl;
  @Input() public specialInstructions: string =
    this.accountDTO.specialInstructions || '';
  @Input() public ssoId: string = this.accountDTO.ssoId;
  @Input() public unsubscribeEmail: boolean = this.accountDTO
    .unsubscribeEmailAddress;
  @Input() public unsubscribeNewsletter: boolean = this.accountDTO
    .unsubscribeNewsletter;
  @Input() public username: string = this.accountDTO.username;

  private get accountDTO(): AccountDTO {
    return (window as any).__GLOBALS__.accountDTO;
  }

  // other
  public passedData: { username?: string };
  public get logoUrl() {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/public/login/new-glasses.svg'
    );
  }

  public get userHasAcceptedPrivacyPolicy(): boolean {
    return this.accountDTO.osanoCookieBannerEnabled;
  }

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public webEnvironmentService: WebEnvironmentService,
    private trackerService: TrackerService,
    private route: ActivatedRoute
  ) {}

  public ngOnInit(): void {
    if (this.hasUnsubscribed === true) {
      this.specialInstructions = this.translateService.instant(
        'AccountSvc_Unsubscribe',
        {
          email: this.unsubscribeEmail,
          newsletter: this.unsubscribeNewsletter,
        }
      );
    }

    if (this.message && this.message.length) {
      this.specialInstructions += this.message;
    }

    // This is not an ideal solution.  Previously, special instructions for the org invite were set via ASP Routes.
    // Since the org invite is now being handled by an Angular route ( not attached to an ASP route ) we need to check
    // the return url for the presence of the invite route
    if (this.route.snapshot.queryParams?.ReturnUrl?.indexOf('/invite/') === 0) {
      this.specialInstructions = 'OrgInvite';
    }

    // This dgx object is loaded through AJS, and the init for the NGX app does not
    // get run from the /account/login route, so we have to do this stuff manually
    // to match what the CSHTML page was doing.

    // Once the login pages are migrated, it can be handled by that SPA

    forkJoin([
      this.trackerService.trackPage('account/login'),
      this.trackerService.trackIdentify(),
    ]).subscribe();

    this.cdr.detectChanges();
  }

  public setUtm() {
    // If utm params were passed as part of the query string, we need to parse them
    if (window.location.search.length > 0) {
      const queryString = window.location.search.substring(1);
      const queryStrings = queryString.split('&');
      if (queryStrings.length > 0) {
        let queryStringParams: any = {},
          pair,
          d = decodeURIComponent;

        queryStrings.map((queryStringItem) => {
          pair = queryStringItem.split('=');
          if (pair.length > 1) {
            queryStringParams[d(pair[0].toLowerCase())] = d(pair[1]);
          }
        });

        // DOM access because fields are generated in cshtml via @Html.Hidden
        setTimeout(() => {
          (document.getElementById('utm_source') as HTMLInputElement).value =
            queryStringParams.utm_source;
          (document.getElementById('utm_medium') as HTMLInputElement).value =
            queryStringParams.utm_medium;
          (document.getElementById('utm_campaign') as HTMLInputElement).value =
            queryStringParams.utm_campaign;
        });
      }
    }
  }

  public preserveReturnUrl() {
    // Trickiness to add the url fragment onto the return url since it is lost server side, but present in the url bar due to a redirect.
    setTimeout(() => {
      const returnUrlInput: HTMLInputElement = document.getElementById(
        'returnUrl'
      ) as HTMLInputElement;

      returnUrlInput.value = returnUrlInput.value + window.location.hash;
    });
  }

  public goToState({
    toState,
    passedData,
  }: {
    toState: string;
    passedData?: any;
  }) {
    if (toState) {
      const stateLowered = toState.toLowerCase();
      this.passedData = passedData ?? undefined;
      this.isForgotPassword = stateLowered === 'forgot' ?? false;
      this.isLogin = stateLowered === 'login' ?? false;
      this.isSignup = stateLowered === 'signup' ?? false;
    }
    this.cdr.detectChanges();
  }
}
